import './toast.scss';

import { ReactNode, useRef } from 'react';

import { locator } from '../../testing/e2e-locator/locator';
import { useExitAnimation } from '../hooks/useExitAnimation';
import {
  TOAST_AUTO_HIDE_DURATION,
  TOAST_EXIT_DURATION,
} from './constants/toastTiming';
import { useAutoHideTimer } from './hooks/useAutoHideTimer';
import { useDismissListeners } from './hooks/useDismissListeners';
import { ToastProps } from './types/ToastProps';

export const Toast = ({
  autoHideDuration = TOAST_AUTO_HIDE_DURATION,
  children,
  onClose,
  open,
  placement = 'center',
}: ToastProps): ReactNode => {
  const rootRef = useRef<HTMLDivElement>(null);
  const { pause, resume } = useAutoHideTimer({
    autoHideDuration,
    onClose,
    open,
  });
  const { content, handleAnimationEnd, visible } = useExitAnimation({
    children,
    exitDuration: TOAST_EXIT_DURATION,
    open,
  });

  useDismissListeners({ onClose, open, rootRef });

  const className = ['toast', `toast-${placement}`, !open && 'toast-exiting']
    .filter(Boolean)
    .join(' ');

  if (!visible) {
    return null;
  }

  return (
    <div
      className={className}
      data-testid={locator.components.toast.root}
      ref={rootRef}
      onAnimationEnd={handleAnimationEnd}
      onBlur={resume}
      onFocus={pause}
      onMouseEnter={pause}
      onMouseLeave={resume}
    >
      <div className="toast-content">{content}</div>
    </div>
  );
};
